import React from 'react';
import clsx from 'clsx';
import { LeftCircleOutlined } from '@ant-design/icons';
import { ArrowRightOutlined } from '@ant-design/icons';
import {
  FieldProps,
  TransportationProps,
  ButtonLabels,
} from 'enums/TransportationEnums';
import styles from '../Transportation.module.scss';
import COButton from 'shared/components/COButton';
import {
  CategoryOptions,
  ingredientTransportation,
  transformedDataTransportation,
} from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import COSelect from 'shared/components/COSelect';
import { useFormikContext } from 'formik';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import COCheckbox from 'shared/components/COCheckbox';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { TemperatureControl } from '../TransportationConstants';
import { handleSaveAsDraftForTransportation } from '../handleSaveAsDraftForTransportation';

interface TransportationIngredientListInterface {
  handleGoBack: () => void;
  buttonLoading: boolean;
  isDisabled: boolean;
  handleOpenCancelModal: () => void;
  draftButtonLoading: boolean;
  handleSaveAsDraft: (payload: transformedDataTransportation) => void;
  formData?: transformedDataTransportation;
  handleOpenCreateSupplierModal: () => void;
  supplierOptionsList: CategoryOptions[];
  setSupplierDetails: (fieldName: string) => void;
  fetchData: (params?: { 'input-field': string }) => void;
}

const TransportationIngredientList: FC<
  TransportationIngredientListInterface
> = props => {
  const formikProps = useFormikContext<transformedDataTransportation>();
  const navigate = useNavigate();

  const {
    handleGoBack,
    buttonLoading,
    isDisabled,
    handleOpenCancelModal,
    draftButtonLoading,
    handleSaveAsDraft,
    formData,
    handleOpenCreateSupplierModal,
    supplierOptionsList,
    setSupplierDetails,
    fetchData,
  } = props;

  const handleTopBackButton = () => navigate(-1);

  return (
    <div>
      <div className={styles.formHeaderContainer}>
        <div className={styles.formHeader}>
          <p className={styles.backButton} onClick={handleGoBack}>
            <LeftCircleOutlined />
          </p>
          <h2>{TransportationProps.TRANSPORTATION_HEADING}</h2>
        </div>

        <div className={styles.formButtonContainer}>
          <COButton submit loading={buttonLoading}>
            {ButtonLabels.NEXT}
            <ArrowRightOutlined />
          </COButton>
        </div>
      </div>
      <div className={styles.easeDiv}>
        {isDisabled === true ? (
          <div
            className={clsx({
              [styles.topRightContainer]: true,
              [styles.backDisabledState]: isDisabled,
            })}>
            <COButton text onClick={handleTopBackButton}>
              Back
            </COButton>
          </div>
        ) : isDisabled === false ? (
          <div className={styles.topRightContainer}>
            <COButton text onClick={handleOpenCancelModal}>
              {ButtonLabels.CANCEL}
            </COButton>
            <COButton
              loading={draftButtonLoading}
              secondary
              disabled={isDisabled}
              onClick={() =>
                handleSaveAsDraftForTransportation(
                  formikProps,
                  handleSaveAsDraft
                )
              }>
              {ButtonLabels.SAVE_AS_DRAFT}
            </COButton>
          </div>
        ) : isDisabled === null ? null : null}
      </div>

      <div className={styles.ingredientsFormContainer}>
        <h2>{TransportationProps.INGREDIENT_HEADING}</h2>
        {formData?.ingredients.map(
          (data: ingredientTransportation, index: number) => (
            <div key={`ingredient-${index}`}>
              {data.multipleOrigin?.map((_, originIndex) => (
                <React.Fragment key={`ingredient-origin-${originIndex}`}>
                  <div className={styles.fieldRow}>
                    <div className={originIndex !== 0 ? styles.hide : ''}>
                      <p className={styles.pointer}>{index + 1}</p>
                    </div>
                    {/* Ingredient Name */}
                    <div
                      className={clsx({
                        [styles.bigFieldContainer]: true,
                        [styles.ingredientName]:
                          FieldProps.INGREDIENT_NAME ===
                          FieldProps.INGREDIENT_NAME,
                        [styles.hide]: originIndex !== 0,
                      })}>
                      <COSelect
                        key={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_NAME}`}
                        name={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_NAME}`}
                        label={FieldProps.INGREDIENT_NAME_PLACEHOLDER}
                        placeholderstring={
                          FieldProps.INGREDIENT_NAME_PLACEHOLDER
                        }
                        disabled={isDisabled ? true : true}
                      />
                    </div>
                    {/* Ingredient Origin Field */}
                    <div
                      className={clsx({
                        [styles.bigFieldContainer]: true,
                        [styles.ingredientName]:
                          FieldProps.INGREDIENT_NAME ===
                          FieldProps.INGREDIENT_NAME,
                      })}>
                      <COSelect
                        key={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.ORIGIN}`}
                        name={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.ORIGIN}`}
                        label={FieldProps.INGREDIENT_ORIGIN_PLACEHOLDER}
                        placeholderstring={
                          FieldProps.INGREDIENT_ORIGIN_PLACEHOLDER
                        }
                        disabled={isDisabled ? true : true}
                      />
                    </div>
                    {/* Supplier Field */}
                    <div
                      className={clsx({
                        [styles.bigFieldContainer]: true,
                        [styles.ingredientName]:
                          FieldProps.INGREDIENT_NAME ===
                          FieldProps.INGREDIENT_NAME,
                      })}>
                      <COSelect
                        name={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.SUPPLIER_NAME}`}
                        key={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.SUPPLIER_NAME}`}
                        label={FieldProps.SUPPLIER_PLACEHOLDER}
                        placeholderstring={FieldProps.SUPPLIER_PLACEHOLDER}
                        options={supplierOptionsList}
                        disabled={isDisabled ? true : false}
                        onFocus={() => {
                          setSupplierDetails(
                            `${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.SUPPLIER_NAME}`
                          );
                          fetchData?.({
                            'input-field': 'packaging material name',
                          });
                        }}
                        dropdownRender={(menu: CategoryOptions[]) => {
                          return (
                            <>
                              <div
                                className={styles.dropdownCreateButton}
                                onClick={handleOpenCreateSupplierModal}>
                                <Add /> {TransportationProps.CREATE_SUPPLIER}
                              </div>
                              {menu}
                            </>
                          );
                        }}
                      />
                    </div>
                    {/* Temparature Control Field */}
                    <div
                      className={clsx({
                        [styles.bigFieldContainer]: true,
                        [styles.ingredientName]:
                          FieldProps.INGREDIENT_NAME ===
                          FieldProps.INGREDIENT_NAME,
                      })}>
                      <COSelect
                        key={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.TEMPERATURE_CONTROL}`}
                        name={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.TEMPERATURE_CONTROL}`}
                        label={FieldProps.TEMPERATUR_CONTROL_PLACEHOLDER}
                        placeholderstring={
                          FieldProps.TEMPERATUR_CONTROL_PLACEHOLDER
                        }
                        options={TemperatureControl}
                        disabled={isDisabled ? true : false}
                        onFocus={() =>
                          fetchData({
                            'input-field': 'ingredient - Temperature Control',
                          })
                        }
                      />
                    </div>
                    <div className={styles.checboxContainer}>
                      <COCheckbox
                        name={`${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.VIA_AIR}`}
                        onChange={e =>
                          formikProps.setFieldValue(
                            `${TransportationProps.INGREDIENTS}[${index}].${FieldProps.MULTIPLE_ORIGIN}[${originIndex}].${FieldProps.VIA_AIR}`,
                            e.target.checked
                          )
                        }
                        disabled={isDisabled ? true : false}
                        label={
                          <div className={styles.checkboxLabel}>
                            <p>{FieldProps.VIA_AIR_LABEL}</p>
                          </div>
                        }
                      />
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TransportationIngredientList;
